import React, { useMemo } from "react";
import { motion } from "motion/react";
import { Target, Users, TrendingUp, Award, Activity, Zap } from "lucide-react";

const AnimatedCounter = ({ value, duration = 1 }) => {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    let startTime;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / (duration * 1000), 1);

      setCount(Math.floor(progress * value));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [value, duration]);

  return <span>{count}</span>;
};

const TrendBadge = ({ trend }) => {
  if (!trend || trend === 0) return null;

  const isPositive = trend > 0;

  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-semibold ${
        isPositive
          ? "text-emerald-600 dark:text-emerald-400"
          : "text-red-600 dark:text-red-400"
      }`}
    >
      {isPositive ? "↑" : "↓"} {Math.abs(trend).toFixed(1)}
    </span>
  );
};

const MetricCard = ({
  icon: Icon,
  label,
  value,
  suffix = "",
  trend,
  color = "orange",
  delay = 0,
}) => {
  const colorClasses = {
    orange: {
      bg: "from-orange-50 to-amber-50 dark:from-orange-950/40 dark:to-amber-950/30",
      border: "border-orange-200 dark:border-orange-800",
      icon: "text-orange-600 dark:text-orange-400",
      text: "text-orange-600 dark:text-orange-400",
    },
    blue: {
      bg: "from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30",
      border: "border-blue-200 dark:border-blue-800",
      icon: "text-blue-600 dark:text-blue-400",
      text: "text-blue-600 dark:text-blue-400",
    },
    purple: {
      bg: "from-purple-50 to-violet-50 dark:from-purple-950/40 dark:to-violet-950/30",
      border: "border-purple-200 dark:border-purple-800",
      icon: "text-purple-600 dark:text-purple-400",
      text: "text-purple-600 dark:text-purple-400",
    },
    emerald: {
      bg: "from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30",
      border: "border-emerald-200 dark:border-emerald-800",
      icon: "text-emerald-600 dark:text-emerald-400",
      text: "text-emerald-600 dark:text-emerald-400",
    },
    amber: {
      bg: "from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-yellow-950/30",
      border: "border-amber-200 dark:border-amber-800",
      icon: "text-amber-600 dark:text-amber-400",
      text: "text-amber-600 dark:text-amber-400",
    },
    rose: {
      bg: "from-rose-50 to-pink-50 dark:from-rose-950/40 dark:to-pink-950/30",
      border: "border-rose-200 dark:border-rose-800",
      icon: "text-rose-600 dark:text-rose-400",
      text: "text-rose-600 dark:text-rose-400",
    },
  };

  const colors = colorClasses[color] || colorClasses.orange;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
      whileHover={{ y: -4, scale: 1.02 }}
      className={`bg-gradient-to-br ${colors.bg} rounded-2xl p-6 border-2 ${colors.border} 
                  transition-all shadow-sm hover:shadow-lg`}
    >
      <div className="flex items-start justify-between mb-4">
        <div
          className={`flex h-12 w-12 items-center justify-center rounded-2xl bg-white dark:bg-gray-900 
                        border ${colors.border} shadow-sm`}
        >
          <Icon size={24} className={colors.icon} />
        </div>
        {trend !== undefined && <TrendBadge trend={trend} />}
      </div>

      <div className={`text-4xl font-black ${colors.text} mb-1 tabular-nums`}>
        {typeof value === "number" ? (
          <>
            <AnimatedCounter value={value} />
            {suffix}
          </>
        ) : (
          <>
            {value}
            {suffix}
          </>
        )}
      </div>

      <p className="text-sm font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wider">
        {label}
      </p>
    </motion.div>
  );
};

const PerformanceMetrics = ({ players, finishedGames, team }) => {
  const metrics = useMemo(() => {
    if (!players?.length || !finishedGames?.length) {
      return {
        totalPlayers: players?.length || 0,
        gamesPlayed: 0,
        avgPoints: 0,
        avgRebounds: 0,
        topScorer: null,
        teamEfficiency: 0,
      };
    }

    // Calculate team averages
    let totalTeamPoints = 0;
    let totalTeamRebounds = 0;
    let totalTeamAssists = 0;

    finishedGames.forEach((game) => {
      const isHome = game.team1?.id === team?.id;
      totalTeamPoints += isHome ? game.team1_score || 0 : game.team2_score || 0;
    });

    // Calculate player stats
    const playersWithStats = players.map((player) => {
      const stats = player.game_stats || [];
      const validStats = stats.filter(
        (s) => s.points > 0 || s.rebounds > 0 || s.assists > 0,
      );

      const totalPoints = validStats.reduce(
        (sum, s) => sum + (s.points || 0),
        0,
      );
      const totalRebounds = validStats.reduce(
        (sum, s) => sum + (s.rebounds || 0),
        0,
      );
      const totalAssists = validStats.reduce(
        (sum, s) => sum + (s.assists || 0),
        0,
      );

      totalTeamRebounds += totalRebounds;
      totalTeamAssists += totalAssists;

      return {
        ...player,
        totalPoints,
        totalRebounds,
        totalAssists,
        gamesPlayed: validStats.length,
        ppg: validStats.length > 0 ? totalPoints / validStats.length : 0,
      };
    });

    const topScorer = playersWithStats.reduce(
      (max, p) => (p.ppg > (max?.ppg || 0) ? p : max),
      null,
    );

    const gamesPlayed = finishedGames.length;
    const avgPoints = gamesPlayed > 0 ? totalTeamPoints / gamesPlayed : 0;
    const avgRebounds = gamesPlayed > 0 ? totalTeamRebounds / gamesPlayed : 0;
    const avgAssists = gamesPlayed > 0 ? totalTeamAssists / gamesPlayed : 0;

    // Simple efficiency metric (points + rebounds + assists per game)
    const teamEfficiency = avgPoints + avgRebounds + avgAssists;

    return {
      totalPlayers: players.length,
      gamesPlayed,
      avgPoints: avgPoints.toFixed(1),
      avgRebounds: avgRebounds.toFixed(1),
      avgAssists: avgAssists.toFixed(1),
      topScorer,
      teamEfficiency: teamEfficiency.toFixed(0),
    };
  }, [players, finishedGames, team?.id]);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-orange-500/10 dark:bg-orange-500/20">
          <Activity
            size={22}
            className="text-orange-600 dark:text-orange-400"
          />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Performance Metrics
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <MetricCard
          icon={Users}
          label="Roster Size"
          value={metrics.totalPlayers}
          color="blue"
          delay={0}
        />

        <MetricCard
          icon={Activity}
          label="Games Played"
          value={metrics.gamesPlayed}
          color="purple"
          delay={0.1}
        />

        <MetricCard
          icon={Target}
          label="Points Per Game"
          value={metrics.avgPoints}
          color="orange"
          delay={0.2}
        />

        <MetricCard
          icon={TrendingUp}
          label="Rebounds Per Game"
          value={metrics.avgRebounds}
          color="emerald"
          delay={0.3}
        />

        <MetricCard
          icon={Zap}
          label="Assists Per Game"
          value={metrics.avgAssists}
          color="amber"
          delay={0.4}
        />

        <MetricCard
          icon={Award}
          label="Team Efficiency"
          value={metrics.teamEfficiency}
          color="rose"
          delay={0.5}
        />
      </div>

      {/* Top Performer Highlight */}
      {metrics.topScorer && metrics.topScorer.ppg > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 
                     dark:from-orange-950/40 dark:via-amber-950/30 dark:to-yellow-950/20
                     rounded-3xl p-6 border-2 border-orange-200 dark:border-orange-800"
        >
          <div className="flex items-center gap-3 mb-3">
            <Award className="text-orange-600 dark:text-orange-400" size={24} />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Top Performer
            </h3>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-black text-gray-900 dark:text-white">
                {metrics.topScorer.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Leading scorer • {metrics.topScorer.gamesPlayed} games played
              </p>
            </div>
            <div className="text-right">
              <p className="text-4xl font-black text-orange-600 dark:text-orange-400">
                {metrics.topScorer.ppg.toFixed(1)}
              </p>
              <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400 mt-1">
                PPG
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default PerformanceMetrics;
